import type {
  ActiveLineForAgent,
  AuditLogEntry,
  BackupFileInfo,
  CallOutcome,
  CarrierTest,
  PhoneLine,
  PhoneLineDailyStat,
  PhoneLineEvent,
  PhoneLineSummary,
  PhoneLineWarning,
  ReputationCheck,
  CallSessionProgress,
  CallingWorkspaceLead,
  CooldownRule,
  Customer,
  DashboardMetrics,
  DuplicateCandidate,
  DuplicateCandidateDetail,
  FollowUp,
  GlobalSearchResult,
  ImportBatch,
  ImportUndoPlan,
  JobRecord,
  LeadDetail,
  LeadListRow,
  OutcomeMapping,
  Paginated,
  Payment,
  WebsiteAuditTabData,
  WebsiteIssueType,
} from '@shared/types.js';
import type {
  AuthState,
  LeadFilter,
  LeadListQuery,
  PublicUser,
  UserPreferences,
} from '@shared/schemas.js';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(message: string, status: number, code: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * The security-relevant parts of every outbound call, in one place.
 *
 * Three code paths issue raw fetches — the JSON wrapper, the CSV downloader and
 * the multipart upload — and each one needs the session cookie and, on a
 * mutation, the CSRF header. Duplicating that in three places is how one of
 * them silently drifts and starts returning 403 to a signed-in user.
 *
 * `isFormData` matters: the browser must set multipart boundaries itself, so
 * Content-Type is deliberately left off.
 */
function requestInit(init: RequestInit | undefined, isFormData = false): RequestInit {
  const method = (init?.method ?? 'GET').toUpperCase();
  return {
    ...init,
    credentials: 'same-origin',
    headers: {
      ...(isFormData || init?.body == null ? {} : { 'Content-Type': 'application/json' }),
      ...(MUTATING_METHODS.has(method) ? { 'X-LeadForge-Request': '1' } : {}),
      ...init?.headers,
    },
  };
}

/**
 * Reachability of the server, and nothing else.
 *
 * Deliberately just two values. "The session expired" and "the link to Ohio
 * dropped" are not two points on one scale — they are different problems with
 * different fixes, and folding them into one enum is how a transpacific hiccup
 * ends up looking like a sign-out. Session state lives in lib/sessionGate.ts
 * and lib/auth.tsx; this is purely "did an HTTP response come back".
 *
 * Any response at all — 200, 401, 500 — proves the server is reachable.
 */
export type ConnectionState = 'ok' | 'offline';

const connectionListeners = new Set<(state: ConnectionState) => void>();

let connectionState: ConnectionState = 'ok';

export function onConnectionChange(listener: (state: ConnectionState) => void): () => void {
  connectionListeners.add(listener);
  return () => connectionListeners.delete(listener);
}

/** Current reachability. Exported for tests and for a first render. */
export function getConnectionState(): ConnectionState {
  return connectionState;
}

function setConnectionState(state: ConnectionState): void {
  if (connectionState === state) return;
  connectionState = state;
  for (const listener of connectionListeners) listener(state);
}

/**
 * Record what an HTTP response tells us.
 *
 * Two independent facts come out of one response: the server answered (so we
 * are online, whatever the status), and — if it answered 401 — the session is
 * gone. Both are reported; neither is inferred from the other.
 */
function noteResponse(status: number): void {
  // A response arrived. Whatever it says, the connection works.
  setConnectionState('ok');
  if (status === 401) {
    for (const listener of unauthorizedListeners) listener();
  }
}

/**
 * Listeners notified when the server reports the session is gone.
 *
 * The auth provider subscribes so it can decide what a 401 MEANS — an
 * anonymous first visit and a session that died mid-call get very different
 * treatment. Nothing here navigates: a redirect from inside the fetch wrapper
 * is how you get redirect loops, and it is also how a VA loses the call notes
 * they were halfway through typing.
 */
const unauthorizedListeners = new Set<() => void>();

export function onUnauthorized(listener: () => void): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

/**
 * fetch(), with a network failure turned into a typed error instead of a raw
 * TypeError. Everything the client shows for "server unreachable" starts here.
 */
async function transport(input: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (err) {
    setConnectionState('offline');
    throw new ApiError(
      'Cannot reach LeadForge. Check your connection and try again.',
      0,
      'offline',
      err,
    );
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await transport(`/api${path}`, requestInit(init, init?.body instanceof FormData));

  noteResponse(response.status);

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  if (!response.ok) {
    if (contentType.includes('application/json')) {
      const body = (await response.json()) as { message?: string; error?: string; details?: unknown };
      throw new ApiError(
        body.message ?? 'Request failed',
        response.status,
        body.error ?? 'error',
        body.details,
      );
    }
    throw new ApiError(await response.text(), response.status, 'error');
  }

  if (contentType.includes('application/json')) return (await response.json()) as T;
  return (await response.text()) as T;
}

const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
const patch = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
const put = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PUT', body: JSON.stringify(body) });
const del = <T>(path: string) => request<T>(path, { method: 'DELETE' });

/** Download a CSV response as a file. */
async function download(path: string, init: RequestInit, fallbackName: string): Promise<void> {
  // Lead export is a POST, so it is a mutation as far as the CSRF rule is
  // concerned and must carry the header. Report downloads are GETs and are
  // unaffected. Both need the cookie.
  const response = await transport(`/api${path}`, requestInit(init));
  noteResponse(response.status);
  if (!response.ok) {
    throw new ApiError(`Export failed (${response.status})`, response.status, 'export_failed');
  }
  const disposition = response.headers.get('content-disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = match?.[1] ?? fallbackName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export type EmailProspectStatus = 'new' | 'contacted' | 'unsubscribed' | 'bounced';
export interface EmailProspectDto {
  id: number;
  reason: string;
  email: string;
  businessName: string;
  city: string | null;
  state: string | null;
  county: string | null;
  category: string | null;
  source: string;
  status: EmailProspectStatus;
  discoveredAt: number;
}
export interface EmailProspectFilterDto {
  q?: string;
  state?: string;
  category?: string;
  status?: EmailProspectStatus;
}
export interface EmailProspectCountsDto {
  total: number;
  byStatus: Record<EmailProspectStatus, number>;
  byState: { state: string; count: number }[];
  byCategory: { category: string; count: number }[];
}

export const api = {
  health: () => request<{ ok: boolean; version: string }>('/health'),

  // ---- Auth ---------------------------------------------------------------
  me: () => request<AuthState>('/auth/me'),
  login: (username: string, password: string) =>
    post<{ user: PublicUser }>('/auth/login', { username, password }),
  logout: () => post<{ ok: boolean }>('/auth/logout', {}),
  changePassword: (currentPassword: string, newPassword: string) =>
    post<{ user: PublicUser }>('/auth/change-password', { currentPassword, newPassword }),

  // ---- Own preferences ----------------------------------------------------
  // Self routes. The user is taken from the session, so there is no id here
  // and no way to read or write anyone else's.
  myPreferences: () => request<UserPreferences>('/auth/me/preferences'),
  updateMyPreferences: (changes: Partial<UserPreferences>) =>
    patch<UserPreferences>('/auth/me/preferences', changes),

  // ---- Users and assignments (admin) --------------------------------------
  listUsers: () => request<{ users: (PublicUser & { activeAssignments: number })[] }>('/users'),
  createUser: (body: unknown) => post<{ user: PublicUser }>('/users', body),
  setUserActive: (id: number, isActive: boolean) => post<{ user: PublicUser }>(`/users/${id}/active`, { isActive }),
  resetUserPassword: (id: number, password: string) =>
    post<{ user: PublicUser }>(`/users/${id}/reset-password`, { password }),
  setAuthEnforcement: (enabled: boolean) => put<{ authEnabled: boolean }>('/auth/enforcement', { enabled }),
  leadAssignment: (leadId: number) =>
    request<{ active: AssignmentDto | null; history: AssignmentDto[] }>(`/leads/${leadId}/assignment`),
  /** Admin-only. Everything the assignment screen reads, in one request. */
  assignmentOverview: (params: { userId?: number; search?: string; hasOwnerName?: boolean; opportunityType?: import('@shared/opportunities.js').OpportunityType; limit?: number } = {}) => {
    const query = new URLSearchParams();
    if (params.userId !== undefined) query.set('userId', String(params.userId));
    if (params.search) query.set('search', params.search);
    if (params.hasOwnerName !== undefined) query.set('hasOwnerName', String(params.hasOwnerName));
    if(params.opportunityType)query.set('opportunityType',params.opportunityType);
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    const suffix = query.toString();
    return request<AssignmentOverviewDto>(`/assignments/overview${suffix ? `?${suffix}` : ''}`);
  },
  assignLead: (leadId: number, userId: number) => post<unknown>('/assignments', { leadId, userId }),
  assignLeads: (leadIds: number[], userId: number) => post<BulkAssignResult>('/assignments/bulk', { leadIds, userId }),
  assignByFilter: (filter: LeadFilter, userId: number) =>
    post<BulkAssignResult>('/assignments/by-filter', { filter, userId }),
  unassignLeads: (leadIds: number[]) => post<{ requested: number; unassigned: number }>('/assignments/unassign', { leadIds }),
  unassignByFilter: (filter: LeadFilter) => post<{ requested: number; unassigned: number }>('/assignments/unassign', { filter }),
  unassignUserLeads: (userId: number) => post<{ requested: number; unassigned: number }>('/assignments/unassign', { userId }),

  // ---- Phone lines --------------------------------------------------------
  //
  // Everything except `myPhoneLine` is admin-only on the server. The client
  // hides these screens from a VA as a courtesy; the refusal is server-side.
  myPhoneLine: () => request<{ line: ActiveLineForAgent | null }>('/phone-lines/mine'),
  phoneLines: (includeRetired = false) =>
    request<{
      lines: PhoneLineSummary[];
      provider: { kind: string; capabilities: Record<string, boolean> };
      integrations: {
        telnyx: {
          configured: boolean;
          connectionConfigured: boolean;
          telephonyCredentialConfigured: boolean;
          callerNumberConfigured: boolean;
          reputationEnterpriseConfigured: boolean;
          numberProvisioning: boolean;
          reputationChecks: boolean;
          voiceCallingIntegrated: boolean;
        };
      };
    }>(`/phone-lines?includeRetired=${includeRetired}`),
  phoneLineWarnings: () => request<{ warnings: PhoneLineWarning[] }>('/phone-lines/warnings'),
  telnyxStatus: () =>
    request<{
      telnyx: {
        configured: boolean;
        connectionConfigured: boolean;
        telephonyCredentialConfigured: boolean;
        callerNumberConfigured: boolean;
        reputationEnterpriseConfigured: boolean;
        numberProvisioning: boolean;
        reputationChecks: boolean;
        voiceCallingIntegrated: boolean;
        account: {
          enterpriseAccess: 'available' | 'blocked' | 'unknown';
          reputationAccess: 'ready' | 'setup_required' | 'blocked' | 'unknown';
          reason: string | null;
        } | null;
      };
    }>('/phone-lines/providers/telnyx/status'),
  telnyxWebrtcToken: () =>
    post<{ token: string; callerNumber: string; callerName: string | null }>('/phone-lines/providers/telnyx/webrtc-token'),
  searchTelnyxNumbers: (params: {
    areaCode?: string;
    locality?: string;
    administrativeArea?: string;
    limit?: number;
  }) => {
    const query = new URLSearchParams();
    if (params.areaCode) query.set('areaCode', params.areaCode);
    if (params.locality) query.set('locality', params.locality);
    if (params.administrativeArea) query.set('administrativeArea', params.administrativeArea);
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    return request<{
      numbers: Array<{
        phoneNumber: string;
        upfrontCost: string | null;
        monthlyCost: string | null;
        currency: string | null;
        features: string[];
        regions: string[];
        reservable: boolean;
        quickship: boolean;
      }>;
    }>(`/phone-lines/providers/telnyx/available?${query.toString()}`);
  },
  orderTelnyxNumber: (body: { phoneNumber: string; label?: string }) =>
    post<{
      line: PhoneLine;
      order: {
        id: string;
        phoneNumber: string;
        providerPhoneNumberId: string | null;
        status: string | null;
      };
      identitySetup: {
        attempted: boolean;
        success: boolean;
        cnam: string | null;
        error: string | null;
      };
    }>('/phone-lines/providers/telnyx/order', body),
  telnyxCallerIdentity: (id: number) =>
    request<{
      identity: {
        providerPhoneNumberId: string;
        phoneNumber: string | null;
        purchasedAt: string | null;
        cnamListingEnabled: boolean;
        cnam: string | null;
        inboundCallerNameLookupEnabled: boolean;
        account: {
          enterpriseAccess: 'available' | 'blocked' | 'unknown';
          reputationAccess: 'ready' | 'setup_required' | 'blocked' | 'unknown';
          reason: string | null;
        };
      };
    }>('/phone-lines/' + id + '/provider-identity'),
  updateTelnyxCnam: (id: number, cnam: string) =>
    patch<{
      identity: {
        providerPhoneNumberId: string;
        phoneNumber: string | null;
        purchasedAt: string | null;
        cnamListingEnabled: boolean;
        cnam: string | null;
        inboundCallerNameLookupEnabled: boolean;
        account: {
          enterpriseAccess: 'available' | 'blocked' | 'unknown';
          reputationAccess: 'ready' | 'setup_required' | 'blocked' | 'unknown';
          reason: string | null;
        };
      };
    }>('/phone-lines/' + id + '/provider-identity/cnam', { cnam }),
  createPhoneLine: (body: unknown) => post<{ line: PhoneLine }>('/phone-lines', body),
  updatePhoneLine: (id: number, body: unknown) => patch<{ line: PhoneLine }>(`/phone-lines/${id}`, body),
  setPhoneLineStatus: (id: number, status: string, reason?: string | null) =>
    post<{ line: PhoneLine }>(`/phone-lines/${id}/status`, { status, reason: reason ?? null }),
  rotatePhoneLine: (id: number, backupLineId: number, reason?: string | null) =>
    post<{ pausedLine: PhoneLine; promotedLine: PhoneLine }>(`/phone-lines/${id}/rotate`, {
      backupLineId,
      reason: reason ?? null,
    }),
  flagAndRotatePhoneLine: (id: number, backupLineId: number, reason?: string | null) =>
    post<{ flaggedLine: PhoneLine; promotedLine: PhoneLine }>('/phone-lines/' + id + '/flag-and-rotate', {
      backupLineId,
      reason: reason ?? null,
    }),
  phoneLineEvents: (id: number) => request<{ events: PhoneLineEvent[] }>(`/phone-lines/${id}/events`),
  phoneLineStats: (id: number) => request<{ stats: PhoneLineDailyStat[] }>(`/phone-lines/${id}/stats`),
  reputationChecks: (id: number) =>
    request<{ checks: ReputationCheck[] }>(`/phone-lines/${id}/reputation-checks`),
  recordReputationCheck: (id: number, body: unknown) =>
    post<{ check: ReputationCheck }>(`/phone-lines/${id}/reputation-checks`, body),
  refreshTelnyxReputation: (id: number, fresh = false) =>
    post<{
      check: ReputationCheck;
      reputation: {
        phoneNumber: string;
        spamRisk: 'low' | 'medium' | 'high' | null;
        spamCategory: string | null;
        maturityScore: number | null;
        connectionScore: number | null;
        engagementScore: number | null;
        sentimentScore: number | null;
        lastRefreshedAt: string | null;
      };
    }>(`/phone-lines/${id}/reputation-checks/telnyx`, { fresh }),
  carrierTests: (id: number) => request<{ tests: CarrierTest[] }>(`/phone-lines/${id}/carrier-tests`),
  recordCarrierTest: (id: number, body: unknown) =>
    post<{ test: CarrierTest }>(`/phone-lines/${id}/carrier-tests`, body),
  rollupPhoneLineStats: (body: unknown = {}) =>
    post<{ dates: string[]; linesTouched: number; rowsWritten: number }>('/phone-lines/rollup', body),

  // ---- Leads --------------------------------------------------------------
  listLeads: (query: Partial<LeadListQuery>) => post<Paginated<LeadListRow>>('/leads/list', query),
  getLead: (id: number) => request<LeadDetail>(`/leads/${id}`),
  createLead: (body: unknown) => post<{ id: number }>('/leads', body),
  updateLead: (id: number, body: unknown) => patch<unknown>(`/leads/${id}`, body),
  setLeadStatus: (id: number, pipelineStatus: string, note?: string) =>
    post<unknown>(`/leads/${id}/status`, { pipelineStatus, note }),
  setLeadDnc: (id: number, doNotContact: boolean, reason?: string | null) =>
    post<unknown>(`/leads/${id}/dnc`, { doNotContact, reason }),
  archiveLead: (id: number, archived: boolean) => post<unknown>(`/leads/${id}/archive`, { archived }),
  search: (q: string) => request<GlobalSearchResult>(`/leads/search?q=${encodeURIComponent(q)}`),
  addNote: (leadId: number, noteBody: string, noteType = 'General') =>
    post<unknown>(`/leads/${leadId}/notes`, { noteBody, noteType }),
  updateNote: (noteId: number, noteBody: string) => patch<unknown>(`/notes/${noteId}`, { noteBody }),
  deleteNote: (noteId: number) => del<void>(`/notes/${noteId}`),
  exportLeads: (query: Partial<LeadListQuery>) =>
    download('/leads/export', { method: 'POST', body: JSON.stringify(query) }, 'leads.csv'),
  mergeLeads: (sourceId: number, targetLeadId: number, fieldChoices?: Record<string, 'source' | 'target'>) =>
    post<unknown>(`/leads/${sourceId}/merge`, { targetLeadId, fieldChoices }),

  // ---- Reference ----------------------------------------------------------
  categories: () => request<(Category & { leadCount: number })[]>('/categories'),
  createCategory: (body: unknown) => post<unknown>('/categories', body),
  updateCategory: (id: number, body: unknown) => patch<unknown>(`/categories/${id}`, body),
  deleteCategory: (id: number) => del<unknown>(`/categories/${id}`),
  counties: () => request<(County & { leadCount: number })[]>('/counties'),
  createCounty: (body: unknown) => post<unknown>('/counties', body),
  updateCounty: (id: number, body: unknown) => patch<unknown>(`/counties/${id}`, body),
  deleteCounty: (id: number) => del<unknown>(`/counties/${id}`),
  savedFilters: () => request<SavedFilterDto[]>('/saved-filters'),
  createSavedFilter: (body: unknown) => post<unknown>('/saved-filters', body),
  deleteSavedFilter: (id: number) => del<void>(`/saved-filters/${id}`),

  // ---- Websites and audits -------------------------------------------------
  leadWebsites: (leadId: number) => request<unknown[]>(`/leads/${leadId}/websites`),
  addWebsite: (leadId: number, body: unknown) => post<unknown>(`/leads/${leadId}/websites`, body),
  setPrimaryWebsite: (leadId: number, websiteId: number) =>
    post<unknown>(`/leads/${leadId}/websites/${websiteId}/primary`),
  updateWebsite: (id: number, body: unknown) => patch<unknown>(`/websites/${id}`, body),
  deleteWebsite: (id: number) => del<void>(`/websites/${id}`),
  setNoWebsite: (leadId: number, confirmed: boolean) =>
    post<{ webPresenceStatus: string }>(`/leads/${leadId}/no-website`, { confirmed }),
  auditTab: (leadId: number) => request<WebsiteAuditTabData>(`/leads/${leadId}/audit`),
  recordFinding: (leadId: number, body: unknown) =>
    post<{ auditId: number; warnings: string[] }>(`/leads/${leadId}/findings`, body),
  setIssueStatus: (issueId: number, issueStatus: string, resolutionNotes?: string) =>
    post<unknown>(`/website-issues/${issueId}/status`, { issueStatus, resolutionNotes }),
  issueTypes: () => request<WebsiteIssueType[]>('/website-issue-types'),
  updateIssueType: (id: number, body: unknown) => patch<unknown>(`/website-issue-types/${id}`, body),
  websiteConditions: () => request<{ conditions: { value: string; tooltip: string }[] }>('/website-conditions'),

  // ---- Imports -------------------------------------------------------------
  uploadImport: async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    // isFormData: the browser sets the multipart boundary; setting
    // Content-Type by hand here produces a body the server cannot parse.
    const response = await transport('/api/imports/upload', requestInit({ method: 'POST', body: form }, true));
    noteResponse(response.status);
    if (!response.ok) {
      const body = (await response.json()) as { message?: string; error?: string };
      throw new ApiError(body.message ?? 'Upload failed', response.status, body.error ?? 'error');
    }
    return (await response.json()) as ImportPreviewDto;
  },
  importFields: () => request<{ fields: { field: string; label: string }[] }>('/imports/fields'),
  commitImport: (id: number, body: unknown) => post<ImportResultDto>(`/imports/${id}/commit`, body),
  listImports: (params?: { fileName?: string; sourceKind?: string; checksum?: string }) => {
    const query = new URLSearchParams();
    if (params?.fileName) query.set('fileName', params.fileName);
    if (params?.sourceKind) query.set('sourceKind', params.sourceKind);
    if (params?.checksum) query.set('checksum', params.checksum);
    const suffix = query.toString();
    return request<Paginated<ImportBatch>>(`/imports${suffix ? `?${suffix}` : ''}`);
  },
  /** Watched-folder outcomes, including checksum-skipped files. */
  watchedFiles: (limit = 25) =>
    request<{ rows: WatchedFileDto[] }>(`/watched-files?limit=${limit}`),
  getImport: (id: number) => request<{ import: ImportBatch; rows: ImportRowDto[] }>(`/imports/${id}`),
  undoPlan: (id: number) => request<ImportUndoPlan>(`/imports/${id}/undo-plan`),
  undoImport: (id: number, rowIds?: number[]) => post<UndoResultDto>(`/imports/${id}/undo`, { rowIds }),
  deleteImport: (id: number) => del<void>(`/imports/${id}`),
  mappingTemplates: () => request<MappingTemplateDto[]>('/mapping-templates'),
  deleteMappingTemplate: (id: number) => del<void>(`/mapping-templates/${id}`),
  purgeRawImportRows: () => post<{ purged: number }>('/imports/purge-raw'),

  // ---- Duplicates ----------------------------------------------------------
  listDuplicates: (body: unknown) =>
    post<Paginated<DuplicateCandidate & { targetName: string; sourceName: string }>>('/duplicates/list', body),
  duplicateCounts: () => request<{ pending: number; byType: { match_type: string; n: number }[] }>('/duplicates/counts'),
  getDuplicate: (id: number) => request<DuplicateCandidateDetail>(`/duplicates/${id}`),
  resolveDuplicate: (id: number, body: unknown) =>
    post<{ resolution: string; message: string; leadId: number | null }>(`/duplicates/${id}/resolve`, body),
  bulkDuplicates: (body: unknown) => post<BulkDuplicateResult>('/duplicates/bulk', body),
  scanDuplicates: () => post<{ scanned: number; created: number; skippedSuppressed: number }>('/duplicates/scan'),

  // ---- Calling -------------------------------------------------------------
  callOutcomes: () =>
    request<{ outcomes: CallOutcome[]; mappings: OutcomeMapping[]; cooldowns: CooldownRule[] }>('/call-outcomes'),
  updateOutcomeMapping: (outcomeId: number, body: unknown) =>
    put<unknown>(`/call-outcomes/${outcomeId}/mapping`, body),
  updateCooldown: (outcomeId: number, body: unknown) => put<unknown>(`/call-outcomes/${outcomeId}/cooldown`, body),
  queuePresets: () => request<QueuePresetDto[]>('/queue-presets'),
  createQueuePreset: (body: unknown) => post<unknown>('/queue-presets', body),
  deleteQueuePreset: (id: number) => del<void>(`/queue-presets/${id}`),
  callSessions: (includeCompleted = false) =>
    request<CallSessionProgress[]>(`/call-sessions?includeCompleted=${includeCompleted}`),
  createCallSession: (body: unknown) => post<{ id: number }>('/call-sessions', body),
  sessionProgress: (id: number) => request<CallSessionProgress & { removedOnRefilter: number }>(`/call-sessions/${id}`),
  nextInQueue: (id: number) =>
    request<{
      lead: CallingWorkspaceLead | null;
      callUri: string | null;
      protocol: string;
      progress: CallSessionProgress;
    }>(`/call-sessions/${id}/next`),
  sessionItems: (id: number) => request<QueueItemDto[]>(`/call-sessions/${id}/items`),
  pauseSession: (id: number) => post<unknown>(`/call-sessions/${id}/pause`),
  resumeSession: (id: number) => post<CallSessionProgress & { removedOnRefilter: number }>(`/call-sessions/${id}/resume`),
  abandonSession: (id: number) => post<unknown>(`/call-sessions/${id}/abandon`),
  skipQueueItem: (id: number, reason?: string) => post<unknown>(`/queue-items/${id}/skip`, { reason }),
  callingLead: (leadId: number, sessionId?: number) =>
    request<{ lead: CallingWorkspaceLead; callUri: string | null; protocol: string }>(
      `/calling/lead/${leadId}${sessionId ? `?sessionId=${sessionId}` : ''}`,
    ),
  recordCall: (body: unknown) => post<RecordCallResultDto>('/call-attempts', body),

  // ---- Follow-ups and pipeline ---------------------------------------------
  followUps: () => request<FollowUpBucketsDto>('/follow-ups'),
  createFollowUp: (body: unknown) => post<FollowUp>('/follow-ups', body),
  updateFollowUp: (id: number, body: unknown) => patch<FollowUp>(`/follow-ups/${id}`, body),
  snoozeFollowUp: (id: number, snoozedUntilDate: string, snoozedUntilTime?: string | null) =>
    post<FollowUp>(`/follow-ups/${id}/snooze`, { snoozedUntilDate, snoozedUntilTime }),
  completeFollowUp: (id: number, body: unknown) => post<unknown>(`/follow-ups/${id}/complete`, body),
  deleteFollowUp: (id: number) => del<void>(`/follow-ups/${id}`),
  pipeline: () => request<{ columns: PipelineColumnDto[] }>('/pipeline'),

  // ---- Customers and money -------------------------------------------------
  customers: () => request<Customer[]>('/customers'),
  customer: (id: number) => request<{ customer: Customer; payments: Payment[] }>(`/customers/${id}`),
  leadCustomer: (leadId: number) => request<{ customer: Customer | null; payments: Payment[] }>(`/leads/${leadId}/customer`),
  createCustomer: (body: unknown) => post<Customer>('/customers', body),
  updateCustomer: (id: number, body: unknown) => patch<Customer>(`/customers/${id}`, body),
  addPayment: (customerId: number, body: unknown) => post<Payment>(`/customers/${customerId}/payments`, body),
  deletePayment: (id: number) => del<void>(`/payments/${id}`),
  moneyTotals: () => request<MoneyTotalsDto>('/customers/totals'),

  // ---- Reports and dashboard -----------------------------------------------
  dashboard: () => request<DashboardMetrics>('/dashboard'),
  reports: (from?: string, to?: string) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    return request<ReportsDto>(`/reports${qs ? `?${qs}` : ''}`);
  },
  exportReport: (report: string, from?: string, to?: string) => {
    const params = new URLSearchParams({ report });
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return download(`/reports/export?${params.toString()}`, { method: 'GET' }, `${report}.csv`);
  },
  operationsOverview: (userId?: number, date?: string) => {
    const params = new URLSearchParams();
    if (userId) params.set('userId', String(userId));
    if (date) params.set('date', date);
    const qs = params.toString();
    return request<OperationsOverviewDto>('/operations/overview' + (qs ? '?' + qs : ''));
  },
  exportOperationsDaily: (userId?: number, date?: string) => {
    const params = new URLSearchParams();
    if (userId) params.set('userId', String(userId));
    if (date) params.set('date', date);
    const qs = params.toString();
    return download('/operations/export' + (qs ? '?' + qs : ''), { method: 'GET' }, 'leadforge-va-daily.csv');
  },
  operationsReliability: () => request<ReliabilityOverviewDto>('/operations/reliability'),
  emailProspectCounts: () => request<EmailProspectCountsDto>('/email-prospects/counts'),
  emailProspects: (filter: EmailProspectFilterDto & { limit?: number; offset?: number }) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filter)) if (value !== undefined && value !== '') params.set(key, String(value));
    const qs = params.toString();
    return request<{ rows: EmailProspectDto[]; total: number }>('/email-prospects' + (qs ? '?' + qs : ''));
  },
  setEmailProspectStatus: (id: number, status: EmailProspectStatus) => patch<EmailProspectDto>(`/email-prospects/${id}`, { status }),
  exportEmailProspects: (filter: EmailProspectFilterDto) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filter)) if (value !== undefined && value !== '') params.set(key, String(value));
    const qs = params.toString();
    return download('/email-prospects/export' + (qs ? '?' + qs : ''), { method: 'GET' }, 'leadforge-email-list.csv');
  },
  operationRecording: (recordingId: string) =>
    request<{ url: string; durationMillis: number | null }>(`/operations/recordings/${encodeURIComponent(recordingId)}`),

  // ---- Settings, jobs, backups ---------------------------------------------
  settings: () => request<{ settings: Record<string, unknown>; defaults: Record<string, unknown> }>('/settings'),
  updateSetting: (key: string, value: unknown) => put<unknown>(`/settings/${key}`, { value }),
  jobs: () => request<JobRecord[]>('/jobs'),
  jobStatus: () =>
    request<{
      scoring: JobRecord | null;
      summaries: JobRecord | null;
      watchedFolder: { running: boolean; paths: Record<string, string> };
    }>('/jobs/status/summary'),
  startRescore: () => post<JobRecord>('/jobs/rescore'),
  startSummaryBackfill: () => post<JobRecord>('/jobs/recompute-summaries'),
  cancelJob: (id: number) => post<JobRecord>(`/jobs/${id}/cancel`),
  resumeJob: (id: number) => post<JobRecord>(`/jobs/${id}/resume`),
  startWatchedFolder: () => post<{ started: boolean }>('/watched-folder/start'),
  stopWatchedFolder: () => post<{ stopped: boolean }>('/watched-folder/stop'),
  workerErrors: () => request<WorkerErrorDto[]>('/worker-errors'),
  acknowledgeWorkerErrors: () => post<{ acknowledged: number }>('/worker-errors/acknowledge-all'),
  backups: () => request<BackupFileInfo[]>('/backups'),
  createBackup: () => post<BackupResultDto>('/backups'),
  validateBackup: (fileName: string) => post<ValidationDto>('/backups/validate', { fileName }),
  verifyBackupRestore: (fileName?: string) =>
    post<RestoreReadinessDto>('/backups/verify-restore', fileName ? { fileName } : {}),
  restoreBackup: (backupFile: string) =>
    post<{ restoredFrom: string; safetyBackup: string; leadCount: number }>('/settings/restore', {
      backupFile,
      confirm: true,
    }),
  deleteBackup: (fileName: string) => del<void>(`/backups/${encodeURIComponent(fileName)}`),
  rebuildSearch: () => post<{ indexed: number }>('/settings/rebuild-search'),
  auditLog: (page = 1) => request<Paginated<AuditLogEntry>>(`/audit-log?page=${page}`),
};

// ---------------------------------------------------------------------------
// Response shapes used only by the client
// ---------------------------------------------------------------------------

export interface Category {
  id: number;
  name: string;
  slug: string;
  isActive: boolean;
  sortOrder: number;
}
export interface County {
  id: number;
  name: string;
  state: string;
  /** 5-digit state+county FIPS code; null for a hand-created county. */
  fipsCode: string | null;
  isActive: boolean;
  sortOrder: number;
  /** Leads physically located in this county. */
  leadCount?: number;
  /** Leads that serve this county, wherever they are based. */
  serviceLeadCount?: number;
}
/** One watched-folder file outcome, including checksum-skipped deliveries. */
export interface WatchedFileDto {
  id: number;
  fileName: string;
  checksum: string;
  byteSize: number;
  importId: number | null;
  /** imported | skipped_duplicate | failed */
  outcome: string;
  message: string | null;
  importStatus: string | null;
  processedAt: number;
}

export interface SavedFilterDto {
  id: number;
  name: string;
  description: string | null;
  filter: LeadFilter;
  sortBy: string | null;
  sortDir: string | null;
}
export interface ImportPreviewDto {
  importId: number;
  fileName: string;
  fileKind: 'csv' | 'json' | 'jsonl';
  headers: string[];
  sampleRows: Record<string, string>[];
  totalSampledRows: number;
  suggestedMapping: Record<string, string | null>;
  duplicateOfImportId: number | null;
  duplicateOfFileName: string | null;
}
export interface ImportResultDto {
  importId: number;
  totalRows: number;
  inserted: number;
  updated: number;
  duplicateCandidates: number;
  skipped: number;
  rejected: number;
  warnings: number;
}
export interface ImportRowDto {
  id: number;
  rowNumber: number;
  action: string;
  leadId: number | null;
  duplicateCandidateId: number | null;
  businessName: string | null;
  message: string | null;
  warnings: string[];
  rolledBackAt: number | null;
}
export interface UndoResultDto {
  rolledBack: number;
  deletedLeads: number;
  restoredLeads: number;
  blocked: number;
}
export interface MappingTemplateDto {
  id: number;
  name: string;
  fileKind: string;
  mapping: { fields: Record<string, string | null>; constants?: Record<string, unknown> };
}
export interface BulkDuplicateResult {
  processed: number;
  merged: number;
  rejected: number;
  suppressed: number;
  blocked: { candidateId: number; reason: string }[];
}
export interface QueuePresetDto {
  id: number;
  name: string;
  description: string | null;
  filter: LeadFilter;
  orderBy: string;
  maxLeads: number;
}
export interface QueueItemDto {
  id: number;
  leadId: number;
  businessName: string;
  primaryPhone: string | null;
  city: string | null;
  position: number;
  state: string;
  removalReason: string | null;
  consumedAt: number | null;
}
export interface RecordCallResultDto {
  callAttemptId: number;
  appliedStatus: string | null;
  appliedDnc: boolean;
  cooldownUntil: number | null;
  followUpId: number | null;
  suggestedFollowUp: { type: string; dueDate: string } | null;
  triggersCustomerConversion: boolean;
}
export interface FollowUpBucketsDto {
  today: string;
  overdue: FollowUp[];
  dueToday: FollowUp[];
  upcoming: FollowUp[];
  snoozed: FollowUp[];
  completed: FollowUp[];
  counts: { overdue: number; dueToday: number; upcoming: number; snoozed: number };
}
export interface PipelineColumnDto {
  status: string;
  total: number;
  leads: {
    id: number;
    businessName: string;
    city: string | null;
    primaryPhone: string | null;
    ownerName: string | null;
    normalizedLeadScore: number | null;
    doNotContact: boolean;
    openFollowUps: number;
    hasCustomer: boolean;
    updatedAt: number;
  }[];
}
export interface MoneyTotalsDto {
  bookedSalesCents: number;
  saleCount: number;
  collectedPaymentsCents: number;
  outstandingBalanceCents: number;
}
export interface ReportBucketDto {
  key: string;
  label: string;
  count: number;
  extra?: Record<string, number>;
}
export interface ReportsDto {
  newLeadsByDate: ReportBucketDto[];
  leadsByCategory: ReportBucketDto[];
  leadsByCounty: ReportBucketDto[];
  leadsByServiceCounty: ReportBucketDto[];
  leadsByCity: ReportBucketDto[];
  leadsByWebPresence: ReportBucketDto[];
  leadsByWebsiteCondition: ReportBucketDto[];
  leadsByWebsiteIssue: ReportBucketDto[];
  leadsByIssueSeverity: ReportBucketDto[];
  leadsByScoreBand: ReportBucketDto[];
  leadsByPipelineStatus: ReportBucketDto[];
  callsByDate: ReportBucketDto[];
  callsByOutcome: ReportBucketDto[];
  callEffectiveness: {
    totalAttempts: number;
    contactedAttempts: number;
    contactRate: number;
    leadsAttempted: number;
    leadsContacted: number;
    leadsInterested: number;
    interestRate: number;
    averageAttemptsBeforeContact: number | null;
    approximateTalkTimeSeconds: number;
    durationIsApproximate: true;
  };
  pipelineConversion: ReportBucketDto[];
  money: MoneyTotalsDto;
  sourcePerformance: ReportBucketDto[];
  importOutcomes: ReportBucketDto[];
  duplicatesPrevented: ReportBucketDto[];
}
export interface OperationsOverviewDto {
  date: string;
  timezone: string;
  isToday: boolean;
  users: Array<{ id: number; username: string; displayName: string; agentId: number | null; isActive: boolean }>;
  selectedUserId: number | null;
  summary: null | {
    displayName: string;
    loggedInAt: number | null;
    lastActivityAt: number | null;
    leadsViewed: number;
    callsAttempted: number;
    callsConnected: number;
    talkTimeSeconds: number;
    noAnswer: number;
    callbacks: number;
    notInterested: number;
    sold: number;
    badLead: number;
    notesAdded: number;
    followUpsCreated: number;
  };
  shift: null | {
    firstActivityAt: number | null;
    lastActivityAt: number | null;
    spanSeconds: number;
    activityGapsOver15m: number;
    longestActivityGapSeconds: number;
    callsPerShiftHour: number;
    leadsPerShiftHour: number;
    callbackTasksCompleted: number;
    callbackTasksOutstanding: number;
    callbackCompletionPercent: number | null;
  };
  timeline: Array<{
    key: string;
    at: number;
    type: string;
    leadId: number | null;
    businessName: string | null;
    summary: string;
  }>;
  calls: null | {
    providerError: string | null;
    callsToday: number;
    connected: number;
    connectedPercent: number;
    averageTalkTimeSeconds: number;
    talkTimeSeconds: number;
    outcomes: Array<{ name: string; count: number }>;
    callsPerHour: Array<{ hour: string; count: number }>;
    leadsProcessed: number;
    callbacksDue: number;
    failedCalls: number;
    qualityFailures: number;
    recent: OperationCallDto[];
  };
}

export interface OperationCallDto {
  key: string;
  callControlId: string | null;
  callLegId: string | null;
  callSessionId: string | null;
  from: string | null;
  to: string | null;
  initiatedAt: number | null;
  answeredAt: number | null;
  endedAt: number | null;
  recordingId: string | null;
  hangupCause: string | null;
  telnyxError: string | null;
  quality: {
    mos: number | null;
    jitterMaxVariance: number | null;
    inboundPackets: number | null;
    inboundSkippedPackets: number | null;
    outboundPackets: number | null;
    outboundSkippedPackets: number | null;
  };
  lead: { id: number; businessName: string } | null;
  disposition: { id: number; leadId: number; outcome: string; notes: string | null } | null;
  durationSeconds: number;
  connected: boolean;
  failed: boolean;
  qualityIssue: boolean;
}

export interface ReliabilityOverviewDto {
  checkedAt: number;
  hostname: string;
  overall: 'ok' | 'warn' | 'down';
  checks: Array<{
    key: string;
    label: string;
    status: 'ok' | 'warn' | 'down';
    detail: string;
    checkedAt: number;
  }>;
}

export interface WorkerErrorDto {
  id: number;
  jobType: string;
  fileName: string | null;
  message: string;
  createdAt: number;
}
/** 201 without the pipeline: the backup file. 202 with the pipeline: a backup set was started in the maintenance thread. */
export type BackupResultDto =
  | {
      fileName: string;
      fullPath: string;
      byteSize: number;
      validated: boolean;
      validationMessage: string | null;
      prunedFiles: string[];
      started?: undefined;
    }
  | { started: true; kind: 'backup' };
export interface ValidationDto {
  ok: boolean;
  message: string;
  integrity: string;
  missingTables: string[];
  leadCount: number | null;
}

export interface RestoreReadinessDto {
  fileName: string;
  testedAt: number;
  byteSize: number;
  leadCount: number;
  integrity: string;
  writable: boolean;
  message: string;
}

export interface AssignmentDto {
  id: number;
  leadId: number;
  userId: number;
  userDisplayName: string | null;
  assignedByUserId: number | null;
  assignedAt: number;
  unassignedAt: number | null;
  note: string | null;
}
export interface BulkAssignResult {
  requested: number;
  assigned: number;
  unchanged: number;
  missing: number;
}

/** One lead as the assignment screen lists it. Mirrors the server's lean row. */
export interface AssignmentLeadDto {
  id: number;
  businessName: string;
  ownerName: string | null;
  city: string | null;
  state: string | null;
  pipelineStatus: LeadListRow['pipelineStatus'];
  normalizedLeadScore: number | null;
  attemptCount: number;
  lastContactedAt: number | null;
}

export interface AssignmentOverviewDto {
  activeTotal: number;
  /** Global unassigned count. Never narrowed by the search. */
  unassignedTotal: number;
  /** Unassigned leads matching the current search; equals the above when none. */
  matchingTotal: number;
  /** Server-resolved, so "assign all matching" acts on what actually matches. */
  unassignedIds: number[];
  unassigned: AssignmentLeadDto[];
  /** Every VA account, active or not. `isActive` means "may receive new work". */
  vas: { userId: number; displayName: string; isActive: boolean; assignedTotal: number }[];
  selected: {
    userId: number;
    displayName: string;
    isActive: boolean;
    assignedTotal: number;
    leads: AssignmentLeadDto[];
  } | null;
}
